import type { SegmentAnalysisLoadStatus } from "../types"

import { AlertTriangle, Loader2 } from "@/components/icons"
import { EmptyState } from "@/components/ui/empty-state"

type SegmentAnalysisBackendStateProps = {
  backendError: string | null
  backendStatus: SegmentAnalysisLoadStatus
}

/**
 * Full-page backend states for live-data-only tenants (no demo dataset
 * opt-in, #2768): a spinner while the portfolio query loads and an honest
 * error state when it fails, instead of silently painting the demo
 * portfolio. Demo tenants never render this — they keep the historical
 * instant render backed by the mock fallback.
 */
export function SegmentAnalysisBackendState({
  backendError,
  backendStatus,
}: SegmentAnalysisBackendStateProps) {
  return (
    <main className="bg-background text-foreground min-h-full" data-testid="segment-analysis-page">
      <div className="module-enter mx-auto max-w-[1480px] px-4 py-10 sm:px-6">
        {backendStatus === "error" ? (
          <section
            className="border-border bg-card rounded-lg border border-dashed px-6 shadow-sm sm:px-12"
            data-testid="sa-backend-error"
          >
            <EmptyState
              className="mx-auto max-w-lg"
              icon={AlertTriangle}
              title="Segments couldn't be loaded"
              description={
                backendError ??
                "The Segment Analysis backend is unavailable. Try again in a moment."
              }
            />
          </section>
        ) : (
          <div
            className="text-muted-foreground flex min-h-[40vh] items-center justify-center gap-2 text-sm"
            data-testid="sa-backend-loading"
          >
            <Loader2 className="size-4 animate-spin" />
            Loading segments…
          </div>
        )}
      </div>
    </main>
  )
}
